import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
// Note: AnimatePresence kept for sub-nav expand/collapse only (not overlay)
import {
  LayoutDashboard,
  Sparkles,
  Palette,
  Image,
  Settings,
  Crown,
  ChevronLeft,
  ChevronDown,
  X,
  Package,
  Mic,
  ShoppingBag,
} from 'lucide-react';
import { cn } from '@soundbyte/config';
import { useUIStore, useSidebar } from '@soundbyte/stores';
import { ROUTES } from '@soundbyte/config/constants';

interface SubNavItem {
  label: string;
  path: string;
  icon: React.ElementType;
}

interface NavItem {
  label: string;
  path: string;
  icon: React.ElementType;
  subItems?: SubNavItem[];
}

const navItems: NavItem[] = [
  { label: 'Dashboard', path: ROUTES.DASHBOARD, icon: LayoutDashboard },
  { label: 'Sonic Identity', path: ROUTES.SONIC, icon: Sparkles },
  // NavLink compares against pathname only, so a query-string path like
  // `/originals?new=true` breaks the active-state indicator. Keep the pathname
  // clean and attach `?new=true` via a separate wrapper if needed later.
  { label: 'SoundBYTE', path: ROUTES.ORIGINALS, icon: Palette },
  { label: 'Shop', path: ROUTES.SHOP, icon: ShoppingBag },
  {
    label: 'My Orders',
    path: ROUTES.ORDERS,
    icon: Package,
    subItems: [
      { label: 'Sonic Identity', path: ROUTES.ORDERS_SONIC, icon: Mic },
      { label: 'SoundBYTE Originals', path: ROUTES.ORDERS_ORIGINALS, icon: Palette },
      { label: 'Art Shop', path: ROUTES.ORDERS_COLLECTION, icon: ShoppingBag },
    ],
  },
  { label: 'Gallery', path: ROUTES.GALLERY, icon: Image },
  { label: 'Subscription', path: ROUTES.SUBSCRIPTION, icon: Crown },
  { label: 'Settings', path: ROUTES.SETTINGS, icon: Settings },
];

export const Sidebar: React.FC = () => {
  const { isOpen, isCollapsed } = useSidebar();
  const { toggleSidebarCollapse, setSidebarOpen } = useUIStore();
  const location = useLocation();

  // Track expanded state for items with sub-navigation
  const [expandedItems, setExpandedItems] = useState<string[]>(() => {
    // Auto-expand if on a sub-route
    const expanded: string[] = [];
    if (location.pathname.startsWith('/orders')) {
      expanded.push(ROUTES.ORDERS);
    }
    return expanded;
  });

  const toggleExpanded = (path: string) => {
    setExpandedItems((prev) =>
      prev.includes(path)
        ? prev.filter((p) => p !== path)
        : [...prev, path]
    );
  };

  const isSubItemActive = (item: NavItem) => {
    return item.subItems?.some((sub) => location.pathname === sub.path);
  };

  // Close sidebar on route change (mobile)
  React.useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname, setSidebarOpen]);

  return (
    <>
      {/* Mobile Overlay - no AnimatePresence to avoid ghost elements blocking clicks */}
      {isOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          className="fixed inset-0 z-40 bg-black/80 backdrop-blur-sm lg:hidden"
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed left-0 top-16 z-40 h-[calc(100vh-4rem)] border-r border-border bg-card transition-all duration-300',
          'lg:sticky lg:translate-x-0',
          isOpen ? 'translate-x-0' : '-translate-x-full',
          isCollapsed ? 'w-16' : 'w-64'
        )}
      >
        {/* Close button (mobile) */}
        <button
          type="button"
          aria-label="Close navigation"
          onClick={() => setSidebarOpen(false)}
          className="absolute right-2 top-2 rounded-md p-1 hover:bg-muted lg:hidden"
        >
          <X className="h-5 w-5" aria-hidden="true" />
        </button>

        {/* Collapse button (desktop) */}
        <button
          type="button"
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-pressed={isCollapsed}
          onClick={toggleSidebarCollapse}
          className={cn(
            'absolute -right-3 top-6 hidden h-6 w-6 items-center justify-center rounded-full border border-border bg-card shadow-sm hover:bg-muted lg:flex',
            isCollapsed && 'rotate-180'
          )}
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
        </button>

        {/* Navigation */}
        <nav className="flex flex-col gap-1 p-3 pt-8" aria-label="Primary">
          {/* Section label */}
          {!isCollapsed && (
            <span className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Menu
            </span>
          )}

          {/* Nav items */}
          {navItems.map((item) => {
            const hasSubItems = item.subItems && item.subItems.length > 0;
            const isExpanded = expandedItems.includes(item.path);
            const hasActiveSubItem = isSubItemActive(item);

            if (hasSubItems && !isCollapsed) {
              // Expandable item with sub-navigation
              return (
                <div key={item.path}>
                  <button
                    type="button"
                    aria-expanded={isExpanded}
                    aria-controls={`sidebar-subnav-${item.path.replace(/[^a-zA-Z0-9]/g, '-')}`}
                    onClick={() => toggleExpanded(item.path)}
                    className={cn(
                      'w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                      hasActiveSubItem
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    )}
                  >
                    <item.icon className="h-5 w-5 shrink-0" aria-hidden="true" />
                    <span className="flex-1 text-left">{item.label}</span>
                    <ChevronDown
                      aria-hidden="true"
                      className={cn(
                        'h-4 w-4 transition-transform',
                        isExpanded && 'rotate-180'
                      )}
                    />
                  </button>

                  {/* Sub-items */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        id={`sidebar-subnav-${item.path.replace(/[^a-zA-Z0-9]/g, '-')}`}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="ml-4 pl-3 mt-1 space-y-1 border-l border-border">
                          {item.subItems?.map((subItem) => (
                            <NavLink
                              key={subItem.path}
                              to={subItem.path}
                              onClick={() => setSidebarOpen(false)}
                              className={({ isActive }) =>
                                cn(
                                  'flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors',
                                  isActive
                                    ? 'bg-primary text-primary-foreground'
                                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                                )
                              }
                            >
                              <subItem.icon className="h-4 w-4 shrink-0" />
                              <span>{subItem.label}</span>
                            </NavLink>
                          ))}
                          {/* All Orders link */}
                          <NavLink
                            to={item.path}
                            end
                            onClick={() => setSidebarOpen(false)}
                            className={({ isActive }) =>
                              cn(
                                'flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors',
                                isActive
                                  ? 'bg-primary text-primary-foreground'
                                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                              )
                            }
                          >
                            <Package className="h-4 w-4 shrink-0" />
                            <span>All Orders</span>
                          </NavLink>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            }

            // Regular nav item (no sub-items or collapsed)
            return (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === ROUTES.DASHBOARD}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                    isActive || (hasSubItems && hasActiveSubItem)
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  )
                }
              >
                <item.icon className="h-5 w-5 shrink-0" />
                {!isCollapsed && <span>{item.label}</span>}
              </NavLink>
            );
          })}
        </nav>

        {/* Bottom section */}
        {!isCollapsed && (
          <div className="absolute bottom-0 left-0 right-0 border-t border-border p-4">
            <div className="rounded-lg bg-gradient-to-br from-primary/20 to-primary/5 p-4">
              <p className="text-sm font-medium text-foreground">
                Need help?
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Have questions or need assistance?
              </p>
              <a
                href="mailto:contact@artbyamrita.com"
                className="mt-2 inline-block text-xs font-medium text-primary hover:underline"
              >
                contact@artbyamrita.com
              </a>
            </div>
          </div>
        )}
      </aside>
    </>
  );
};
